/*
  Corestride cart extras, injected into Dawn's cart drawer and cart page so the stock cart snippets stay untouched
  (the Atlas-style cart from Nathan Miller's course, 3:40:12):
  - a bundle progress bar (1 sleeve -> 3 with 1 free -> 5 with 2 free) instead of a free-shipping bar, since
    shipping in Canada is already free on every order;
  - the "add more, the next one is free" upsell;
  - an opt-in shipping protection toggle (product "shipping-protection", unchecked by default);
  - a trust row with payment icons.
  Free sleeves must match the automatic "buy X get Y" discounts "Buy 2, Get 1 Free" (3 sleeves, 1 free) and
  "Buy 3, Get 2 Free" (5 sleeves, 2 free), each used once per order (Nathan Miller's BOGO offer [4:22:46]).
  Copy (English / French) comes from window.csStrings, set in sections/cs-brand.liquid.
*/
(() => {
  const HANDLE = 'figure-8-ankle-sleeve';
  const PROTECT_HANDLE = 'shipping-protection';
  const freeAt = (qty) => (qty >= 5 ? 2 : qty >= 3 ? 1 : 0);
  const priceAt = (unit, qty) => unit * (qty - freeAt(qty));
  const root = window.Shopify?.routes?.root || '/';
  const money = (cents) => window.csMoney(cents);
  const S = window.csStrings || {};
  const fill = (text, vars) => String(text || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));

  const containers = () =>
    [document.querySelector('cart-drawer .drawer__footer'), document.querySelector('#main-cart-footer .cart__blocks')].filter(Boolean);

  // shipping protection variant (id + price), read once from the product JSON so the price lives in Shopify only
  let protect = null;
  const loadProtect = () =>
    (protect ||= fetch(`${root}products/${PROTECT_HANDLE}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => (p && p.variants[0] ? { id: p.variants[0].id, price: p.variants[0].price } : null))
      .catch(() => null));

  const sleeveLines = (cart) => cart.items.map((item, i) => ({ ...item, line: i + 1 })).filter((item) => item.handle === HANDLE);
  const protectLine = (cart) => cart.items.find((item) => item.handle === PROTECT_HANDLE);

  function progressHtml(qty) {
    const step = qty >= 5 ? 3 : qty >= 3 ? 2 : 1;
    const msg = qty >= 5 ? S.progress5 : S[`progress${qty}`];
    return `<div class="cs-progress" style="--cs-step:${step}; --cs-fill:${Math.min(qty - 1, 4) * 25}%">
      <p class="cs-progress__msg">${msg}</p>
      <div class="cs-progress__track"><span class="cs-progress__fill"></span></div>
      <ol class="cs-progress__steps">
        <li class="${step >= 1 ? 'is-on' : ''}">${S.step1}</li>
        <li class="${step >= 2 ? 'is-on' : ''}">${S.step2}</li>
        <li class="${step >= 3 ? 'is-on' : ''}">${S.step3}</li>
      </ol>
    </div>`;
  }

  // next bundle step: 1-2 sleeves -> 3 (1 free), 3-4 -> 5 (2 free); the cost is what the cart total would move by
  function upsellHtml(lines, qty) {
    if (qty >= 5) return '';
    const unit = lines[0].original_price;
    const current = lines.reduce((n, item) => n + item.final_line_price, 0);
    const next = qty < 3 ? 3 : 5;
    const add = next - qty;
    const nextTotal = priceAt(unit, next);
    const cost = Math.max(nextTotal - current, 0);
    const title = cost === 0 ? S.upsellFreeTitle : next === 3 ? S.upsell1 : S.upsell2;
    const note = fill(S.upsellNote, { n: next, total: money(nextTotal), each: money(Math.floor(nextTotal / next)) });
    const button = cost === 0 ? fill(S.upsellButtonFree, { n: add }) : fill(S.upsellButton, { n: add, price: money(cost) });
    const target = lines[0];
    return `<div class="cs-upsell">
      <div class="cs-upsell__text"><p class="cs-upsell__title">${title}</p><p class="cs-upsell__note">${note}</p></div>
      <button type="button" class="cs-upsell__btn" data-line="${target.line}" data-qty="${target.quantity + add}">${button}</button>
    </div>`;
  }

  function protectHtml(cart, variant) {
    if (!variant) return '';
    const on = Boolean(protectLine(cart));
    return `<label class="cs-protect">
      <svg class="cs-protect__icon" viewBox="0 0 24 26" aria-hidden="true"><path d="M12 1.5 3 5v7.2c0 5.6 3.8 10.4 9 12.3 5.2-1.9 9-6.7 9-12.3V5l-9-3.5Z" fill="currentColor"/><path d="m7.6 13.2 3.1 3.1 5.9-6.4" stroke="#5af06e" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span class="cs-protect__text"><b>${fill(S.protectTitle, { price: money(variant.price) })}</b><small>${S.protectNote}</small></span>
      <input type="checkbox" class="cs-protect__toggle" ${on ? 'checked' : ''} aria-label="${fill(S.protectLabel, { price: money(variant.price) })}">
      <span class="cs-switch" aria-hidden="true"></span>
    </label>`;
  }

  function trustHtml() {
    const icons = document.getElementById('cs-pay-icons');
    return `<ul class="cs-cart-trust">
      ${(S.trust || []).map((item) => `<li>${item}</li>`).join('')}
    </ul>${icons ? `<div class="cs-pay-icons">${icons.innerHTML}</div>` : ''}`;
  }

  // Synchronous: (re)draw the extras for a given cart object. In the drawer the progress bar sits under the
  // header (where the Atlas cart puts its reward bar) so the footer stays short; on the cart page it leads the footer.
  function paint(cart, variant) {
    window.csMarket.currency = cart.currency || window.csMarket.currency;
    const lines = sleeveLines(cart);
    const qty = lines.reduce((n, item) => n + item.quantity, 0);
    const header = document.querySelector('cart-drawer .drawer__header');
    document.querySelectorAll('.cs-cart-extras').forEach((n) => n.remove());
    if (!cart.item_count) return;
    if (header && lines.length) {
      const bar = document.createElement('div');
      bar.className = 'cs-cart-extras cs-cart-extras--progress';
      bar.innerHTML = progressHtml(qty);
      header.after(bar);
    }
    containers().forEach((el) => {
      const inDrawer = Boolean(el.closest('cart-drawer'));
      const top = document.createElement('div');
      top.className = 'cs-cart-extras cs-cart-extras--top';
      top.innerHTML = lines.length ? (inDrawer ? '' : progressHtml(qty)) + upsellHtml(lines, qty) + protectHtml(cart, variant) : '';
      const bottom = document.createElement('div');
      bottom.className = 'cs-cart-extras cs-cart-extras--bottom';
      bottom.innerHTML = trustHtml();
      el.prepend(top);
      el.append(bottom);
    });
  }

  async function post(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || S.errorAdd || 'Unable to update cart. Please try again.');
    return result;
  }
  const getCart = async () => (await fetch(`${root}cart.js?t=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();

  // Fetch the cart, then paint. Each call carries a sequence number so only the newest response paints.
  let seq = 0;
  async function render() {
    if (!containers().length) return;
    const mine = ++seq;
    const [cart, variant] = await Promise.all([getCart(), loadProtect()]);
    // protection never stays alone in a cart once the sleeves are gone
    const lonely = protectLine(cart) && !sleeveLines(cart).length;
    if (lonely) return refresh(await post('cart/change.js', { id: String(variant?.id || protectLine(cart).variant_id), quantity: 0 }));
    if (mine === seq) paint(cart, variant);
  }

  // after our own cart change: paint from the server's answer, let Dawn refresh the drawer, paint again
  async function refresh(cart) {
    const variant = await loadProtect();
    const drawerItems = document.querySelector('cart-drawer-items');
    if (document.querySelector('cart-drawer') && drawerItems && drawerItems.onCartUpdate) {
      seq++; // any render still in flight is now stale
      paint(cart, variant);
      await drawerItems.onCartUpdate();
      seq++;
      paint(await getCart(), variant);
    } else {
      window.location.reload();
    }
  }

  function reportError(error) {
    let message = document.querySelector('[data-cs-cart-error]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.csCartError = '';
      message.setAttribute('role', 'alert');
      (containers()[0] || document.body).prepend(message);
    }
    message.textContent = error.message || S.errorAdd || 'Unable to update cart. Please try again.';
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.cs-upsell__btn');
    if (!button) return;
    if (button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = S.adding;
    try {
      document.querySelector('[data-cs-cart-error]')?.remove();
      await refresh(await post('cart/change.js', { line: Number(button.dataset.line), quantity: Number(button.dataset.qty) }));
    } catch (error) {
      reportError(error);
    } finally {
      button.disabled = false;
      button.textContent = label;
    }
  });

  document.addEventListener('change', async (event) => {
    const toggle = event.target.closest('.cs-protect__toggle');
    if (!toggle) return;
    const requested = toggle.checked;
    toggle.disabled = true;
    try {
    document.querySelector('[data-cs-cart-error]')?.remove();
    const variant = await loadProtect();
    if (!variant) throw new Error(S.errorAdd || 'Unable to update cart. Please try again.');
    if (requested) {
      await post('cart/add.js', { items: [{ id: variant.id, quantity: 1 }] });
      await refresh(await getCart());
    } else {
      await refresh(await post('cart/change.js', { id: String(variant.id), quantity: 0 }));
    }
    } catch (error) {
      toggle.checked = !requested;
      reportError(error);
    } finally {
      toggle.disabled = false;
    }
  });

  // Dawn re-renders the drawer after every cart change; re-inject once its markup settles.
  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(render, 120);
  };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver((mutations) => {
      if (mutations.some((m) => [...m.addedNodes, ...m.removedNodes].some((n) => n.nodeType === 1 && !n.closest?.('.cs-cart-extras') && !n.classList?.contains('cs-cart-extras')))) schedule();
    }).observe(drawer, { childList: true, subtree: true });
  }
  window.csCartRender = render; // lets other scripts (and QA) force a refresh
  render();
})();

